import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CreateFreightPipelineBody,
  CreatedFreightPipeline,
  ProcurementReportResponse,
  VendorPOResponse,
} from './types'

export function useVendorPOs() {
  return useQuery({
    queryKey: ['procurement', 'vendor-pos'],
    queryFn: () => jsonFetch<VendorPOResponse>('/api/reports/vendor-pos'),
    staleTime: 30_000,
  })
}

/**
 * Reorder alerts — served by `/api/reports/procurement` alongside
 * open_pos + unpaid_bills. We pluck just `reorder_alerts` here to keep
 * the Procurement view's two tabs on independent cache keys; the
 * "Open POs" tab already has its own dedicated hook via
 * `/api/reports/vendor-pos`, so this endpoint's other fields are
 * unused by us. If a third consumer (e.g. a restored Unpaid Bills
 * tab) ever materializes, consolidating under one cached query makes
 * sense — but not today.
 */
export function useReorderAlerts() {
  return useQuery({
    queryKey: ['procurement', 'reorder-alerts'],
    queryFn: () =>
      jsonFetch<ProcurementReportResponse>('/api/reports/procurement'),
    staleTime: 60_000,
  })
}

/**
 * Starts a freight pipeline row from the Open POs report — the
 * Procurement-side entry point into Logistics's quote/tender workflow
 * (gap fix). Deliberately its own small mutation, not an import of
 * Logistics's `useCreatePipeline` (cross-module import ban) — see
 * `FreightStatusCell.tsx`'s docstring for the established precedent.
 * Idempotent server-side (see app/services/freight_pipeline.py).
 */
export function useCreateFreightPipeline() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateFreightPipelineBody) =>
      jsonFetch<CreatedFreightPipeline>('/api/freight/pipeline', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['procurement', 'vendor-pos'] })
    },
  })
}
